import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed, toJS } from 'mobx';
import { observer } from 'mobx-react';

import { ConfigurationService } from 'src/core/services/configuration.service';
import { ContextService } from 'src/core/services/context.service';
import { HttpService } from 'src/core/services/http.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { DocumentTemplateFormView } from './document-template-form.view';
import { DocumentTemplateDataService } from './document-template-form.data';
import { CustomTemplateItemDataService } from './parts/custom-template-list.data';
import { IdentityService } from 'src/core/services/identity.service';
import { DocumentTemplateBrandDataService } from "./brands/brand-form.data";
import { DocumentTemplateMarketDataService } from "./markets/market-form.data";

interface IDocumentTemplateFormParams {
	id: string,
}

@observer
export class DocumentTemplateFormController extends React.Component<ControllerProps<IDocumentTemplateFormParams>, any> {
	@resolve
	configurationService: ConfigurationService;

	@resolve
	contextService: ContextService;

	@resolve
	httpService: HttpService;

	@resolve
	toast: ToastNotifications;

	@resolve
	identityService: IdentityService;

	@observable
	customTemplatesDataService: CustomTemplateItemDataService;

	@observable
	dataService: DocumentTemplateDataService;

	@observable
	brandDataService: DocumentTemplateBrandDataService;

	@observable
	marketDataService: DocumentTemplateMarketDataService;

	@observable
	defaultLanguage: string;

	@observable
	isAdmin: boolean;

	@observable
	documentServices: DocumentServiceSummary[];

	@observable
	documentDatasets: DocumentDatasetSummary[];

	@observable
	reportsDatasets: DocumentDatasetSummary[];

	@observable
	mode: ControlMode;

	constructor(props: ControllerProps<IDocumentTemplateFormParams>) {
		super(props);

		this.mode = 'display';
	}

	@autobind
	private create() {
		const entity = this.dataService.add(this.defaultLanguage, this.props.match.params.id === "Document");

		return entity;
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();
		this.defaultLanguage = appConfig.defaultLanguage;
	}

	private async loadData(): Promise<void> {
		this.dataService = DocumentTemplateDataService.make(this.contextService);
		this.brandDataService = DocumentTemplateBrandDataService.make(this.contextService);
		this.marketDataService = DocumentTemplateMarketDataService.make(this.contextService);
		this.customTemplatesDataService = CustomTemplateItemDataService.make(this.contextService);

		this.isAdmin = this.identityService.isAdmin;

		if (this.props.match.params.id == 'Document' || this.props.match.params.id == 'Report') {
			// Create new
			await this.dataService.create(this.create);
			this.onModeChange('edit');
		} else {
			// Load entity
			this.dataService.addFilter('id', 'eq', this.props.match.params.id);
			this.dataService.addSorting('id', 'asc');
			this.dataService.pageSize = 1;

			// Load custom templates
			this.customTemplatesDataService.addFilter('templateId', 'eq', this.props.match.params.id);
			if (!this.isAdmin) {
				this.customTemplatesDataService.addFilter('userId', 'eq', this.identityService.userId);
			}
			this.customTemplatesDataService.addSorting('id', 'asc');
			this.customTemplatesDataService.pageSize = 25;

			// Load brands
			this.brandDataService.addFilter('documentTemplateId', 'eq', this.props.match.params.id);
			this.brandDataService.addSorting('id', 'asc');
			this.brandDataService.pageSize = 25;

			// Load markets
			this.marketDataService.addFilter('documentTemplateId', 'eq', this.props.match.params.id);
			this.marketDataService.addSorting('id', 'asc');
			this.marketDataService.pageSize = 25;

			await Promise.all([
				this.dataService.load(query => query),
				this.customTemplatesDataService.load(query => query),
				this.brandDataService.load(query => query),
				this.marketDataService.load(query => query),
			]);

			this.onModeChange('display');
		}
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item && this.dataService.item.dynamicProperties.displayName) {
			return `Document Template ${this.dataService.item.dynamicProperties['displayName'][this.defaultLanguage]}`;
		}

		return '';
	}

	@autobind
	onModeChange(mode: ControlMode) {
		this.mode = mode;
	}

	@autobind
	async save(): Promise<void> {
		const item = this.dataService.item;
		const errors = await item.validate(true);

		if (errors.length == 0) {
			this.props.setBusy(true);

			try {
				await this.dataService.save();
				this.onModeChange('display');
				this.toast.showDataSaved();
				this.props.history.replace(`/general/document-templates`);
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				this.props.setBusy(false);
			}
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	}

	@autobind
	cancel(): void {
		this.props.history.push('/general/document-templates');
	}

	@autobind
	createNewItem(): Promise<ICustomTemplateItem> {

		return null;
	}

	@autobind
	onFileChange(file: File): void {
		if (file) {
			this.props.setBusy(true);

			const contentReader = new FileReader();
			contentReader.onload = (event: Event) => {
				const result: ArrayBuffer = (event.target as any).result;
				this.dataService.item.file = new Uint8Array(result);
				this.dataService.item.dynamicProperties = Object.assign({}, this.dataService.item.dynamicProperties, {
					fileName: file.name
				});

				this.props.setBusy(false);
			};

			const service = this.documentServices.find(s => s.mimeTypes.some(m => m === file.type));
			if (service) {
				this.dataService.item.documentService = service.value;
			}

			contentReader.readAsArrayBuffer(file);
		}
	}

	@autobind
	modalFileChanged(customTemplate: any, file: File) {
		if (file) {
			this.props.setBusy(true);

			const contentReader = new FileReader();
			contentReader.onload = (event: Event) => {
				const result: ArrayBuffer = (event.target as any).result;
				customTemplate.file = new Uint8Array(result);
				this.props.setBusy(false);
			};

			contentReader.readAsArrayBuffer(file);
		}
	}

	@autobind
	private async loadDocumentServices() {
		var result = await this.httpService.get("/api/v1/documents/template/generators");
		this.documentServices = result.data;
	}

	@autobind
	private async loadDocumentDatasets() {
		var result = await this.httpService.get("/api/v1/documents/template/datasets");
		this.documentDatasets = result.data;
	}

	private async loadReportDatasets() {
		var result = await this.httpService.get("/api/v1/documents/template/reports-datasets");
		this.reportsDatasets = result.data;
	}

	componentDidMount() {
		this.props.setBusy(true);

		Promise.all(
			[
				this.loadConfiguration(),
				this.loadData(),
				this.loadDocumentServices()
			])
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
				if (this.dataService.item.isDocument) {
					this.loadDocumentDatasets();
				} else {
					this.loadReportDatasets();
				}
			});
	}

	render() {
		if (
			this.dataService == null
			|| this.dataService.item == null
			|| this.documentServices == null
			|| (this.documentDatasets == null && this.reportsDatasets == null)
		) {
			return null;
		}
		const currentService = this.documentServices.find((t) => t.value === this.dataService.item.documentService);

		return (
			<DocumentTemplateFormView
				title={this.title}
				model={this.dataService.item}
				save={this.save}
				cancel={this.cancel}
				customTemplates={this.customTemplatesDataService}
				modalFileChanged={this.modalFileChanged}
				mode={this.mode}
				createNewItem={this.createNewItem}
				onFileChange={this.onFileChange}
				onModeChange={this.onModeChange}
				setBusy={this.props.setBusy}
				contextService={this.contextService}
				isAdmin={this.isAdmin}
				userId={this.isAdmin ? null : this.identityService.userId}
				documentServices={this.documentServices.map(e => ({ label: e.name, value: e.value, inline: e.inlineDatasetDefinition }))}
				inlineTemplateDatasets={currentService ? currentService.inlineDatasetDefinition : false}
				documentDatasets={toJS(this.documentDatasets, false)}
				reportsDatasets={toJS(this.reportsDatasets, false)}
				brands={this.brandDataService}
				markets={this.marketDataService}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/document-templates/form/document-template-form.controller.tsx